import React, { FunctionComponent, useState, useMemo } from 'react';
import DashboardLayout from '../../../components/layout/layout';

import { styled } from "@mui/material/styles";
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import { Alert, Box, Button, Grid, IconButton, Pagination, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Tooltip } from '@mui/material';
import CheckIcon from "@mui/icons-material/Check";
import { useGetCPAdminCompanyVerificationInfo, useGetCPAdminVerifyUser, usePostCPAdminVerifyUser } from '../../../hooks/api/user';
import { User } from '..';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VerifyUserIcon from '../../../assets/img/Verify User.png';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

const TableCol = styled(TableCell)(({ theme }) => ({
  border: '1px solid rgba(224, 224, 224, 1)',
}));

type SortField = 'firstName' | 'lastName' | 'title' | 'email' | 'phone';
type SortDirection = 'asc' | 'desc' | null;
type SortState = { field: SortField | null; direction: SortDirection };

const AdminVerifyUser: FunctionComponent = () => {
  const navigate = useNavigate();
  const [ showEditUser, setShowEditUser ] = useState(false);

  const { data: companyVerificationInfo, isFetched: hasCompanyInfoFetched } = useGetCPAdminCompanyVerificationInfo();
  const { data: adminUser, isFetched: hasFetched } = useGetCPAdminVerifyUser();
  const { mutateAsync: verifyUser } = usePostCPAdminVerifyUser();

  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>();
  const [ disabled, setDisabled ] = useState<boolean>(false);

  // Client-side pagination and sorting state
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>('firstName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const itemsPerPage = 10;

  // Get all user data without pagination
  const allUserList = useMemo(() => {
    return adminUser || [];
  }, [adminUser]);

  // Sort all data client-side
  const sortedData = useMemo(() => {
    if (!allUserList.length) return [];
    
    return [...allUserList].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];
      aValue = String(aValue || '').toLowerCase();
      bValue = String(bValue || '').toLowerCase();
      if (sortDirection === 'asc') return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    });
  }, [allUserList, sortField, sortDirection]);

  // Paginate sorted data
  const { paginatedData, totalPages } = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = sortedData.slice(startIndex, endIndex);
    const totalPages = Math.ceil(sortedData.length / itemsPerPage);
    return { paginatedData, totalPages };
  }, [sortedData, currentPage, itemsPerPage]);

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Sort icon
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <UnfoldMoreIcon fontSize="small" />;
    if (sortDirection === 'asc') return <ArrowUpwardIcon fontSize="small" />;
    if (sortDirection === 'desc') return <ArrowDownwardIcon fontSize="small" />;
    return <UnfoldMoreIcon fontSize="small" />;
  };

  const navigateUser = (userId: number, navigateTo: string) => {
    if (navigateTo === 'edit') {
      navigate(`/admin/user/edit/${userId}`);
    }
    if (navigateTo === 'delete') {
      navigate(`/admin/user/delete/${userId}`);
    }
  };

  const handleAction = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      setDisabled(true);
      const result = await verifyUser();
      if (result?.status === 200) {
        setSuccess(true)
        setTimeout(() => {
          setSuccess(false);
          navigate('/admin');
          setDisabled(false);
        }, 3000);
      } else {
        setDisabled(false);
        setError('An error occurred during login. Please try again.');
        setTimeout(() => {
          setError('');
        }, 3000);
      }
    } catch (err: any) {
      setDisabled(false);
      const message =
        err?.response?.data?.message ||
        (typeof err?.response?.data === 'string'
          ? err.response.data
          : err?.message) ||
        'An error occurred during login. Please try again.';

      setError(message);
      setTimeout(() => {
        setError('');
      }, 3000);
    }
  }

  return (
    <>
      <DashboardLayout titleIcon={<img src={VerifyUserIcon} alt="Search Inventory" style={{ width: 35, height: 35 }} />} title={"Verify User"} initialBreadcrumbs={"Channel Partner"} levelBreadcrumbs={'CP Admin'} breadcrumbs={'Verify User List'} activeMenu={'admin'}>
        <Typography variant="body1">
          Please verify that the following employees are still employed by your company and should have access to the Extranet.
        </Typography>
        <Typography variant="body1">
          Date Last Verified: {!hasCompanyInfoFetched ? <Skeleton width="38%" /> : <b>{moment(companyVerificationInfo?.lastVerifiedDate).format('MM-DD-YYYY HH:mm:ss')}</b>}
        </Typography>
        <Typography variant="body1" mb={3}>
          Verified By: {!hasCompanyInfoFetched ? <Skeleton width="38%" /> : <b>{`${companyVerificationInfo?.companyName} (${companyVerificationInfo?.verifiedBy})`}</b>}
        </Typography>

        <Box sx={{ mt: 3, display: "flex", alignItems: "center", gap: 3 }}>
          <Button disabled={disabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', minWidth: '100px' }} onClick={handleAction}>
            VERIFY USER
          </Button>
          <Button disabled={disabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', minWidth: '100px' }} onClick={() => setShowEditUser(true)}>
            EDIT USER
          </Button>
        </Box>
        <Grid size={12}>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {
            success && (
              <Alert severity="success" sx={{ mt: 2 }}>
                User verified successfully!
              </Alert>
            )
          }
        </Grid>

        <Box sx={{ width: '100%', maxWidth: '100%', mt: 5 }}>
          <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: "100%", overflowX: "auto", borderRadius: 0 }}>
            <Table size="small" aria-label="user list table">
              <TableHead sx={{ backgroundColor: "#f2f2f2" }}>
                <TableRow>
                  <TableCol
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                    onClick={() => handleSort('firstName')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      First Name
                      <Tooltip title="Click to sort">{getSortIcon('firstName')}</Tooltip>
                    </Box>
                  </TableCol>
                  <TableCol
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                    onClick={() => handleSort('lastName')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      Last Name
                      <Tooltip title="Click to sort">{getSortIcon('lastName')}</Tooltip>
                    </Box>
                  </TableCol>
                  <TableCol
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                    onClick={() => handleSort('title')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      Title
                      <Tooltip title="Click to sort">{getSortIcon('title')}</Tooltip>
                    </Box>
                  </TableCol>
                  <TableCol
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                    onClick={() => handleSort('email')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      Email
                      <Tooltip title="Click to sort">{getSortIcon('email')}</Tooltip>
                    </Box>
                  </TableCol>
                  <TableCol
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                    onClick={() => handleSort('phone')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      Phone
                      <Tooltip title="Click to sort">{getSortIcon('phone')}</Tooltip>
                    </Box>
                  </TableCol>
                  <TableCol>CP Admin</TableCol>
                  <TableCol>Upload Inventory</TableCol>
                  <TableCol>Enter Orders</TableCol>
                  {
                    showEditUser && (
                      <>
                        <TableCol>Edit</TableCol>
                        <TableCol>Delete</TableCol>
                      </>
                    )
                  }
                </TableRow>
              </TableHead>
              <TableBody>
                {
                  hasFetched ? (
                    paginatedData.length === 0 ?
                      <TableRow>
                        <TableCol colSpan={showEditUser ? 10 : 8}>
                          No Data Found
                        </TableCol>
                      </TableRow>
                    : paginatedData.map((user: User, idx: number) => (
                      <TableRow key={user.userId} hover>
                        {/* <TableCol>{idx + 1}.</TableCol> */}
                        <TableCol>{user.firstName}</TableCol>
                        <TableCol>{user.lastName}</TableCol>
                        <TableCol>{user.title || ""}</TableCol>
                        <TableCol>{user.email}</TableCol>
                        <TableCol>{user.phone}</TableCol>
                        <TableCol sx={{ textAlign: "center" }}>
                          {user.cpAdmin && <CheckIcon color="error" />}
                        </TableCol>
                        <TableCol sx={{ textAlign: "center" }}>
                          {user.uploadInventory && <CheckIcon color="error" />}
                        </TableCol>
                        <TableCol sx={{ textAlign: "center" }}>
                          {user.enterOrders && <CheckIcon color="error" />}
                        </TableCol>
                        {
                          showEditUser && (
                            <>
                              <TableCol>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  aria-label="edit user"
                                  onClick={() => navigateUser(user.userId, 'edit')}
                                >
                                  <EditIcon />
                                </IconButton>
                              </TableCol>
                              <TableCol>
                                <IconButton
                                  size="small"
                                  color="error"
                                  aria-label="delete user"
                                  onClick={() => navigateUser(user.userId, 'delete')}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCol>
                            </>
                          )
                        }
                      </TableRow>
                    ))
                  ) : (
                    <>
                      <TableRow>
                        <TableCol colSpan={showEditUser ? 10 : 8}>
                          <Skeleton width="38%" />
                          <Skeleton width="100%" />
                          <Skeleton width="100%" />
                          <Skeleton width="61%" />
                        </TableCol>
                      </TableRow>
                    </>
                  )
                }
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box sx={{ mt: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Button variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important' }} onClick={() => navigate('/admin')}>
            BACK
          </Button>
          {
            totalPages > 0 &&
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(event, value) => {
                handlePageChange(value)
              }}
              siblingCount={1}
              boundaryCount={1}
              shape="rounded" 
              sx={{
                "& .MuiPaginationItem-root.Mui-selected": {
                  backgroundColor: "#c3183c",
                  color: "#fff",
                },
                "& .MuiPaginationItem-root:hover": {
                  color: "#fff",
                  backgroundColor: "#e192a3",
                }
              }} 
            />
          }
        </Box>
      </DashboardLayout>
    </>
  )
};

export default AdminVerifyUser;